import React, { useState } from 'react';
import PropTypes from 'prop-types';
import IntegrationModal from '../CreateIntegration/IntegrationModal';
import { ModalContext } from '../CreateIntegration/ModalContext';
import {
  validateSeedTalentIntegration,
  trimSeedTalentValues
} from '../CreateIntegration/seedTalentFormShared';
import {
  fixedEventTypeFor,
  seedTalentUiTypeFor
} from '../CreateIntegration/seedTalentIntegrationTypes';
import EventsTable from '../EventsTable';
import PerfectScrollbar from 'react-perfect-scrollbar';
import EditIcon from '@mui/icons-material/Edit';
import PlayCircleFilled from '@mui/icons-material/PlayCircleFilled';
import {
  updateIntegration,
  getIntegration,
  runIntegration
} from '../../../../_services/ApiService';
import {
  Card,
  CardActions,
  CardContent,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Snackbar,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogContentText,
  DialogActions,
  styled
} from '@mui/material';
import { ActionCompletedSnackbar } from 'components/ActionCompletedSnackbar';

const Footer = styled(CardActions)({
  justifyContent: 'flex-end'
});

const Content = styled(CardContent)({
  padding: 0
});

const Inner = styled('div')({
  minWidth: 1050
});

const IntegrationsTable = (props) => {
  const {
    className,
    integrations,
    setIntegrations,
    selectedIntegrations,
    setSelectedIntegrations,
    page,
    setPage,
    pageSize,
    setPageSize,
    pageCount,
    ...rest
  } = props;

  const emptyValues = {
    _id: '',
    companyName: '',
    companyId: '',
    eventType: '',
    config: '{ }',
    source: '{ }',
    createdDate: '',
    updatedDate: ''
  };

  const [state, setState] = useState({
    updateIntegrationOpen: false,
    values: emptyValues,
    errors: {},
    errorMessage: ''
  });
  const [uiType, setUiType] = useState('');
  const [showSnackbar, setShowSnackbar] = React.useState(false);

  const handleSelectAll = (event) => {
    const { integrations } = props;

    let selectedIntegrations;

    if (event.target.checked) {
      selectedIntegrations = integrations.map((integration) => integration._id);
    } else {
      selectedIntegrations = [];
    }
    setSelectedIntegrations(selectedIntegrations);
  };

  const closeUpdateIntegration = () => {
    setState({
      updateIntegrationOpen: false,
      values: emptyValues,
      errors: {},
      errorMessage: '',
      seedTalentErrors: {}
    });
    setUiType('');
  };

  const openUpdateIntegration = async (integrationId) => {
    try {
      const integration = await getIntegration(integrationId);
      const newIntegration = {
        ...integration,
        integrationId: integrationId,
        config: JSON.stringify(integration.config, null, 2),
        source: JSON.stringify(integration.source, null, 2)
      };
      setState({
        ...state,
        updateIntegrationOpen: true,
        values: newIntegration
      });
      setUiType(
        seedTalentUiTypeFor(integration.eventType) ||
          (integration.eventType?.startsWith('greenhouse-application-')
            ? 'greenhouse-recruiting'
            : integration.eventType?.startsWith('greenhouse-onboarding-')
              ? 'greenhouse-onboarding'
              : '')
      );
    } catch (err) {
      console.error(err);
      setState({
        ...state,
        errorMessage: err.message
      });
      setShowSnackbar(true);
    }
  };

  const onUpdateIntegration = async (event) => {
    event.preventDefault();
    if (Object.values(state.errors).some((x) => x === true)) {
      return;
    }

    let submitValues = state.values;
    if (fixedEventTypeFor(uiType)) {
      const seedTalentErrors = validateSeedTalentIntegration(state.values);
      if (Object.keys(seedTalentErrors).length > 0) {
        setState({ ...state, seedTalentErrors });
        return;
      }
      submitValues = trimSeedTalentValues(state.values);
    }

    try {
      const saveIntegration = {
        ...submitValues,
        config: JSON.parse(submitValues.config ? submitValues.config : '{ }'),
        source: JSON.parse(submitValues.source ? submitValues.source : '{ }')
      };
      await updateIntegration(state.values._id, saveIntegration);
      const updateIndex = integrations.findIndex(
        (x) => x._id === state.values._id
      );
      const updateCopy = [...integrations];
      updateCopy[updateIndex] = {
        ...updateCopy[updateIndex],
        ...saveIntegration
      };
      setIntegrations(updateCopy);
      closeUpdateIntegration();
    } catch (err) {
      console.error(err);
      setState({
        ...state,
        errorMessage: err.message
      });
    }
    setShowSnackbar(true);
  };

  const handleSelectOne = (_, id) => {
    const selectedIndex = selectedIntegrations.indexOf(id);
    let newSelectedIntegrations = [];

    if (selectedIndex === -1) {
      newSelectedIntegrations = newSelectedIntegrations.concat(
        selectedIntegrations,
        id
      );
    } else if (selectedIndex === 0) {
      newSelectedIntegrations = newSelectedIntegrations.concat(
        selectedIntegrations.slice(1)
      );
    } else if (selectedIndex === selectedIntegrations.length - 1) {
      newSelectedIntegrations = newSelectedIntegrations.concat(
        selectedIntegrations.slice(0, -1)
      );
    } else if (selectedIndex > 0) {
      newSelectedIntegrations = newSelectedIntegrations.concat(
        selectedIntegrations.slice(0, selectedIndex),
        selectedIntegrations.slice(selectedIndex + 1)
      );
    }

    setSelectedIntegrations(newSelectedIntegrations);
  };

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setPageSize(event.target.value);
  };

  const handleRunIntegration = async (integrationId) => {
    setState({
      ...state,
      integrationId: integrationId,
      runDialogOpen: !state.runDialogOpen
    });

    try {
      await runIntegration(integrationId);
    } catch (err) {
      console.error(err);
      setState({
        ...state,
        errorMessage: err.message
      });
    }
  };

  const toggleRunDialog = (integrationId) => {
    setState({
      ...state,
      integrationId: integrationId,
      runDialogOpen: !state.runDialogOpen
    });
  };

  return (
    <Card {...rest}>
      <Content>
        <PerfectScrollbar>
          <Inner>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={
                        selectedIntegrations.length === integrations.length
                      }
                      color="primary"
                      indeterminate={
                        selectedIntegrations.length > 0 &&
                        selectedIntegrations.length < integrations.length
                      }
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Company Name</TableCell>
                  <TableCell>Integration ID</TableCell>
                  <TableCell>Integration Type</TableCell>
                  <TableCell>Run</TableCell>
                  <TableCell>Edit</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {integrations.map((integration) => (
                  <TableRow
                    hover
                    key={integration._id}
                    selected={
                      selectedIntegrations.indexOf(integration._id) !== -1
                    }
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={
                          selectedIntegrations.indexOf(integration._id) !== -1
                        }
                        color="primary"
                        onChange={(event) =>
                          handleSelectOne(event, integration._id)
                        }
                        value="true"
                      />
                    </TableCell>
                    <TableCell>{integration.companyName}</TableCell>
                    <TableCell>{integration._id}</TableCell>
                    <TableCell>{integration.eventType}</TableCell>
                    <TableCell>
                      {integration.runnable && (
                        <IconButton
                          aria-label="run"
                          onClick={() => toggleRunDialog(integration._id)}
                        >
                          <PlayCircleFilled />
                        </IconButton>
                      )}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        aria-label="edit"
                        onClick={() => openUpdateIntegration(integration._id)}
                      >
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <ModalContext.Provider
              value={{
                formState: state,
                setFormState: setState,
                onSubmitIntegration: onUpdateIntegration,
                isUpdate: true,
                uiType,
                setUiType,
                isOpen: state.updateIntegrationOpen,
                onClose: closeUpdateIntegration
              }}
            >
              <IntegrationModal>
                <EventsTable
                  formState={state}
                  integrationId={state.values._id}
                  setFormState={setState}
                  setShowSnackbar={setShowSnackbar}
                />
              </IntegrationModal>
            </ModalContext.Provider>
            <Dialog
              aria-describedby="alert-dialog-description"
              aria-labelledby="alert-dialog-title"
              onClose={toggleRunDialog}
              open={state.runDialogOpen}
            >
              <DialogTitle id="alert-dialog-title">
                {'Run Integration?'}
              </DialogTitle>
              <DialogContent>
                <DialogContentText id="alert-dialog-description">
                  Are you sure you want to run the selected Integration?
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button color="primary" onClick={toggleRunDialog}>
                  Cancel
                </Button>
                <Button
                  autoFocus
                  onClick={() => handleRunIntegration(state.integrationId)}
                >
                  Run
                </Button>
              </DialogActions>
            </Dialog>
          </Inner>
        </PerfectScrollbar>
      </Content>
      <Footer>
        <TablePagination
          component="div"
          count={pageCount}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          page={page}
          rowsPerPage={pageSize}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Footer>
      <ActionCompletedSnackbar
        open={showSnackbar}
        onClose={() => setShowSnackbar(false)}
        onExited={() => setState(state => ({...state, errorMessage: ''}))}
        errorMessage={state.errorMessage}
      />
    </Card>
  );
};

IntegrationsTable.propTypes = {
  integrations: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      companyName: PropTypes.string,
      companyId: PropTypes.string,
      eventType: PropTypes.string,
      runnable: PropTypes.bool,
      config: PropTypes.object,
      source: PropTypes.object,
      createdDate: PropTypes.string,
      updatedDate: PropTypes.string
    })
  ),
  page: PropTypes.number.isRequired,
  pageCount: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  selectedIntegrations: PropTypes.arrayOf(PropTypes.string),
  setIntegrations: PropTypes.func,
  setPage: PropTypes.func.isRequired,
  setPageSize: PropTypes.func.isRequired,
  setSelectedIntegrations: PropTypes.func
};

export default IntegrationsTable;
